/**
 * Studio timetable → working-timetable Excel, in thebigplan's TTBuilder format.
 *
 * Ported from thebigplan-nymr src/pages/ttbuilder/exportXlsx.ts, which
 * replicates the stakeholders' internal WTT workbook ("TT Spring Summer 2026
 * Version 5 Final.xlsx"): one worksheet, a banner per pattern, operating-date
 * notes, then a North and a South sub-block (Headcode / Loco / Guard / Set,
 * the timing-point rows with d/a markers, Forms). Monochrome — Gill Sans MT
 * 8pt, hairline borders, text-format cells, 11pt bold banners. Times are
 * 4-digit tokens ("0930"). ExcelJS is dynamically imported on use, exactly as
 * thebigplan does, so it stays out of the main bundle.
 */
import type ExcelJS from "exceljs"; // types only — the library is dynamically imported on use

export interface TtExportWorking {
  headcode: string; loco: string; guard: string; set: string; forms: string;
  /** 9 timing tokens per the TTBuilder slot order (null = not served). */
  t: (string | null)[];
}
export interface TtExportPattern {
  name: string;
  dates: string[]; // operating-date note lines, e.g. "July   5 . 6 . 12 . 13"
  N: TtExportWorking[];
  S: TtExportWorking[];
}
export interface TtExportData {
  title: string;   // e.g. "JULY 2026 STUDIO TIMETABLE"
  season: string;  // e.g. "2026/2027"
  patterns: TtExportPattern[];
}

// Timing-point rows per direction — index aligns to TtExportWorking.t[]
// (identical to thebigplan ttDerive ROWS; Newtondale is a request halt).
const ROWS: Record<"N" | "S", [string, string][]> = {
  N: [["PIC", "d"], ["LEV", "a"], ["LEV", "d"], ["NEW", "f"], ["GOA", "a"], ["GOA", "d"], ["GRO", "a"], ["GRO", "d"], ["WHB", "a"]],
  S: [["WHB", "d"], ["GRO", "a"], ["GRO", "d"], ["GOA", "a"], ["GOA", "d"], ["NEW", "f"], ["LEV", "a"], ["LEV", "d"], ["PIC", "a"]],
};
const ST_NAME: Record<string, { name: string; halt?: boolean }> = {
  PIC: { name: "Pickering" }, LEV: { name: "Levisham" }, NEW: { name: "Newtondale", halt: true },
  GOA: { name: "Goathland" }, GRO: { name: "Grosmont" }, WHB: { name: "Whitby" },
};

const FONT_NAME = "Gill Sans MT";

export async function exportTimetableXlsx(data: TtExportData): Promise<void> {
  const ExcelJSlib = (await import("exceljs")).default;
  const body = (bold = false): Partial<ExcelJS.Font> => ({ name: FONT_NAME, size: 8, bold });
  const bannerFont: Partial<ExcelJS.Font> = { name: FONT_NAME, size: 11, bold: true };
  const HAIR: Partial<ExcelJS.Border> = { style: "hair" };
  const boxAll = { top: HAIR, bottom: HAIR, left: HAIR, right: HAIR };
  const tb = { top: HAIR, bottom: HAIR };

  const wb = new ExcelJSlib.Workbook();
  wb.creator = "NYMR Reports — Timetable Studio";
  wb.created = new Date();
  const sheetName = data.title.replace(/[\\/?*[\]:]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31);
  const ws = wb.addWorksheet(sheetName || "Working Timetable", { views: [{ showGridLines: false }] });

  // Column widths from the reference workbook (A/U spacers, B/C/D labels, E.. workings).
  ws.getColumn(1).width = 1.29;
  ws.getColumn(2).width = 4.29;
  ws.getColumn(3).width = 5.57;
  ws.getColumn(4).width = 4.57;
  for (let c = 5; c <= 36; c++) ws.getColumn(c).width = 5.14;
  ws.getColumn(21).width = 0.86;

  const maxWork = Math.max(1, ...data.patterns.map((p) => Math.max(p.N.length, p.S.length)));
  const bannerEnd = Math.max(19, 4 + maxWork);

  const setCell = (r: number, c: number, v: string, font: Partial<ExcelJS.Font>, align: "left" | "center", border?: Partial<ExcelJS.Borders>) => {
    const cell = ws.getCell(r, c);
    cell.value = v;
    cell.numFmt = "@";
    cell.font = font;
    cell.alignment = { horizontal: align, vertical: "middle" };
    if (border) cell.border = border;
  };

  let r = 5; // start at row 5, like the reference

  for (const p of data.patterns) {
    // ── banner ──
    ws.mergeCells(r, 3, r, bannerEnd);
    setCell(r, 3, `${p.name.toUpperCase()}     ${data.season}`.trim(), bannerFont, "center");
    ws.getRow(r).height = 12.6;
    r++;

    // ── operating-date notes ──
    for (const line of p.dates) {
      ws.mergeCells(r, 3, r, bannerEnd);
      setCell(r, 3, line, body(), "left");
      ws.getRow(r).height = 9.9;
      r++;
    }
    r++; // blank spacer before the grid

    (["N", "S"] as const).forEach((dir) => {
      const works = p[dir];
      if (works.length === 0) return;
      const rows = ROWS[dir];
      const used = rows.map((_row, i) => works.some((w) => !!w.t[i]));

      const header: [string, (w: TtExportWorking) => string, boolean][] = [
        ["Headcode", (w) => w.headcode, true],
        ["Loco", (w) => w.loco, false],
        ["Guard", (w) => w.guard, false],
        ["Set", (w) => w.set, false],
      ];
      for (const [label, fn, strong] of header) {
        ws.mergeCells(r, 2, r, 3);
        setCell(r, 2, label, body(true), "left", tb);
        ws.getCell(r, 4).border = tb;
        works.forEach((w, j) => setCell(r, 5 + j, fn(w), body(strong), "center", boxAll));
        ws.getRow(r).height = 9.9;
        r++;
      }

      // timing-point rows, station names merged across their a/d pair
      let i = 0;
      while (i < rows.length) {
        const [st] = rows[i];
        if (!used[i]) { i++; continue; }
        const groupRows: number[] = [];
        let j = i;
        while (j < rows.length && rows[j][0] === st) { if (used[j]) groupRows.push(j); j++; }
        const r0 = r, r1 = r + groupRows.length - 1;
        if (groupRows.length > 1) ws.mergeCells(r0, 2, r1, 3); else ws.mergeCells(r0, 2, r0, 3);
        setCell(r0, 2, ST_NAME[st].name + (ST_NAME[st].halt ? " (req)" : ""), body(false), "left", tb);
        groupRows.forEach((slot, gi) => {
          const rr = r0 + gi;
          const [, k] = rows[slot];
          setCell(rr, 4, k === "f" ? "" : k, body(false), "center", tb);
          works.forEach((w, wi) => setCell(rr, 5 + wi, w.t[slot] ?? "", body(false), "center", boxAll));
          ws.getRow(rr).height = 9.9;
        });
        r += groupRows.length;
        i = j;
      }

      // Forms
      ws.mergeCells(r, 2, r, 3);
      setCell(r, 2, "Forms", body(true), "left", tb);
      ws.getCell(r, 4).border = tb;
      works.forEach((w, j) => setCell(r, 5 + j, w.forms, body(false), "center", boxAll));
      ws.getRow(r).height = 9.9;
      r++;
      const lastCol = 4 + works.length;
      for (let c = 21; c < lastCol; c++) if (!ws.getColumn(c + 1).width) ws.getColumn(c + 1).width = 5.14;
      r++; // spacer between N and S
    });

    r += 3; // gap between pattern blocks
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${data.title.replace(/\W+/g, "_")}_WTT.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
